import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AGENT_AI_NOTICE, homeownerAiNoticePlain } from '../shared/consentCopy';

const smallStyle = (color) => ({
  margin: '0.75rem 0 0',
  color: color || '#4b5563',
  fontSize: '0.8rem',
  lineHeight: 1.45,
});

// Tab bar is 1000. Cookie notice is 9999. The sheet has to sit above both.
const SHEET_Z = 13000;

export function homeownerPrivacyLine(sponsorless) {
  return sponsorless
    ? 'Your AI conversations are private to you.'
    : "Your AI conversations are private to you. Your sponsor can't see them.";
}

export function HomeownerAiNoticeText({ sponsorless }) {
  const privacy = homeownerPrivacyLine(sponsorless);
  return (
    <>
      <strong>About the AI Assistant:</strong>
      {" answers are generated by AI (provided by Anthropic) and can be wrong. They're not professional advice. Your questions and any photos you submit are sent to our AI provider to generate a response and are saved in your Pacarion account. "}
      <strong>{privacy}</strong>
      {" (Pacarion may access them only as needed to operate, secure, and support the service.) Don't include sensitive personal information. For safety issues (gas, electrical, structural, mold, fire, carbon monoxide), contact a licensed pro or call 911."}
    </>
  );
}

/**
 * The chat pane traps a fixed layer under the tab bar.
 * Portaling onto the page puts the sheet on top of the bar.
 */
function AiNoticeSheet({ onClose, children }) {
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement;
    if (closeRef.current && closeRef.current.focus) {
      try {
        closeRef.current.focus({ preventScroll: true });
      } catch (error) {
        closeRef.current.focus();
      }
    }
    const onKey = (event) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const scroller = document.querySelector('.app-scroll');
    const previousOverflow = scroller ? scroller.style.overflow : '';
    if (scroller) scroller.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      if (scroller) scroller.style.overflow = previousOverflow;
      if (previous && previous.focus) previous.focus();
    };
  }, []);

  const sheet = (
    <div
      className="ai-notice-sheet"
      onClick={() => onCloseRef.current()}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: SHEET_Z,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        background: 'rgba(15, 23, 42, 0.45)',
      }}
    >
      <style>{`
        .ai-notice-sheet {
          padding-top: max(1rem, env(safe-area-inset-top, 0px));
          padding-right: max(1rem, env(safe-area-inset-right, 0px));
          padding-bottom: max(1rem, env(safe-area-inset-bottom, 0px));
          padding-left: max(1rem, env(safe-area-inset-left, 0px));
        }
        .ai-notice-sheet-card {
          max-height: 48vh;
          max-height: min(48dvh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 2rem));
        }
        .ai-notice-sheet-header {
          padding-left: max(1.25rem, env(safe-area-inset-left, 0px));
        }
        .ai-notice-sheet-body {
          padding-right: max(1.25rem, env(safe-area-inset-right, 0px));
          padding-left: max(1.25rem, env(safe-area-inset-left, 0px));
        }
        .ai-notice-sheet-footer {
          padding-right: max(1.25rem, env(safe-area-inset-right, 0px));
          padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
          padding-left: max(1.25rem, env(safe-area-inset-left, 0px));
        }
        @media (max-width: 767px) {
          .ai-notice-sheet {
            align-items: flex-end !important;
            padding-right: 0 !important;
            padding-bottom: 0 !important;
            padding-left: 0 !important;
          }
          .ai-notice-sheet-card {
            width: 100% !important;
            max-width: 100% !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
          }
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-notice-title"
        className="ai-notice-sheet-card"
        onClick={(event) => event.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          width: 'min(100%, 520px)',
          minHeight: 0,
          overflow: 'hidden',
          background: 'Canvas',
          color: 'CanvasText',
          borderRadius: '1rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div
          className="ai-notice-sheet-header"
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            boxSizing: 'border-box',
            paddingTop: '0.35rem',
            paddingRight: '0.35rem',
            paddingBottom: '0.35rem',
            borderBottom: '1px solid rgba(128, 128, 128, 0.35)',
            background: 'Canvas',
          }}
        >
          <h2 id="ai-notice-title" style={{ margin: 0, fontSize: '1.15rem', lineHeight: 1.3 }}>
            About the AI Assistant
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            onClick={() => onCloseRef.current()}
            style={{
              flex: '0 0 auto',
              width: '44px',
              height: '44px',
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              fontSize: '1.5rem',
              lineHeight: 1,
              cursor: 'pointer',
              borderRadius: '999px',
            }}
          >
            ✕
          </button>
        </div>
        <div
          className="ai-notice-sheet-body"
          data-ai-notice-body="true"
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            boxSizing: 'border-box',
            paddingTop: '1rem',
            paddingBottom: '1rem',
          }}
        >
          <p role="note" style={{ margin: 0, fontSize: '1rem', lineHeight: 1.45 }}>
            {children}
          </p>
        </div>
        <div
          className="ai-notice-sheet-footer"
          style={{
            flex: '0 0 auto',
            boxSizing: 'border-box',
            paddingTop: '0.75rem',
            borderTop: '1px solid rgba(128, 128, 128, 0.35)',
            background: 'Canvas',
          }}
        >
          <button
            type="button"
            onClick={() => onCloseRef.current()}
            style={{
              width: '100%',
              minHeight: '44px',
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: '0.75rem',
              background: '#0f766e',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return sheet;
  return createPortal(sheet, document.body);
}

/**
 * One short line under the input, plus the full notice in a sheet.
 * The long paragraph used to sit in the page scroll and push the input away.
 */
function AiNoticeLine({ color, children }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <p style={{ ...smallStyle(color), margin: 0, display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
        <span>AI answers can be wrong.</span>
        <button
          type="button"
          aria-label="About the AI Assistant"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          style={{
            minHeight: '44px',
            minWidth: '44px',
            padding: '0 0.25rem',
            border: 'none',
            background: 'transparent',
            color: color || '#0f766e',
            fontWeight: 700,
            fontSize: '0.95rem',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          About
        </button>
      </p>
      {open ? <AiNoticeSheet onClose={close}>{children}</AiNoticeSheet> : null}
    </div>
  );
}

export function HomeownerAiNotice({ color, sponsorless }) {
  return (
    <div>
      <AiNoticeLine color={color}>
        <HomeownerAiNoticeText sponsorless={sponsorless} />
      </AiNoticeLine>
      <p style={smallStyle(color)}>{homeownerPrivacyLine(sponsorless)}</p>
    </div>
  );
}

export function AgentAiNotice({ color }) {
  return (
    <AiNoticeLine color={color}>
      {AGENT_AI_NOTICE}
    </AiNoticeLine>
  );
}

export { homeownerAiNoticePlain, AGENT_AI_NOTICE };
